import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { DataService } from 'src/app/shared/data.service';
import { AuthService } from 'src/app/auth/auth.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { AlertDialogComponent } from 'src/app/shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FactoryChild } from '../factory.model';
import { AddEditUserComponent } from './add-edit-user/add-edit-user.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { ExportService } from '../../shared/export.service';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false
})
export class UsersComponent implements OnInit {
  showSearch: boolean;
  searchForm!: FormGroup;
  pageSize = 10;
  pageNumber = 1;
  totalSize = 0;
  userID: number;
  usersList = new Array<FactoryChild>();
  queryParams: {
    pageNumber: number;
    pageSize: number;
    searchValue: string | null;
  } = {
    pageNumber: 0,
    pageSize: 0,
    searchValue: null,
  };

  searchValue = "";
  imageSrc: string;
  currentTheme: string;
  isDistributor = false;

  constructor(private router: Router, public dataService: DataService, private authService: AuthService,
              private route: ActivatedRoute,
              public utils: UtilsService,
              private dialog: MatDialog,
              private exportService: ExportService
              ) {
    if (this.authService.getUserData().type === this.utils.userTypes.Distributor) {
      this.isDistributor = true;
    }
    this.showSearch = false;
    this.userID = this.authService.getUserData().id;
    this.currentTheme = localStorage.getItem('activeTheme') || 'theme-light';
    if (this.currentTheme === 'theme-light') {
      this.imageSrc = '../../../assets/images/distributor-light.png';
    } else {
      this.imageSrc = '../../../assets/images/distributor-dark.png';
    }

    this.route.queryParams.subscribe((params: Params) => {
      if (!this.isEmpty(params)) {
        this.pageNumber = params.pageNumber ? params.pageNumber : 1;
        this.pageSize = params.pageSize ? params.pageSize : 10;
        this.searchValue =  params.searchValue ? params.searchValue : "";
      }
      this.getUsers();
    });
   }

  isEmpty(obj: any) {
    return Object.keys(obj).length === 0;
  }

  ngOnInit(): void {
    this.utils.setTitle('Users');
    this.utils.screenTitle.emit({firstTitle: 'Users', firstRoute: this.isDistributor ? '/manufacturer-users' : '/distributor-users'});
    this.searchForm = new FormGroup({
      search: new FormControl(this.searchValue),
    });

    if (this.searchValue) {
      this.showSearch = true;
    }

    this.searchForm.get('search')?.valueChanges.subscribe(value => {
      const newValue = (value || '').trim();
      if (newValue === '' && this.searchValue !== '') {
        setTimeout(() => this.clearSearch(), 0);
      }
    });

    this.utils.themeName.subscribe((themeName: string) => {
      this.currentTheme = themeName;
      if (this.router.url === '/distributor-users' || this.router.url === '/manufacturer-users') {
        this.getUsers();
      }
    });
  }

  getUsers() {
    let accountType = 'manufacturer/RetrieveManufacturerChildren/';
    if (this.isDistributor) {
      accountType = 'distributor/RetrieveDistributorChildren/';
    }
    this.dataService.get(accountType + this.userID + '?pageNumber=' + this.pageNumber +
    '&pageSize=' + this.pageSize +
    '&searchKeyword=' + this.searchValue).then((data: any) => {
      if (data.statusCode === 200) {
        this.usersList = [];
        const usersData = data.data.records;
        this.totalSize = data.data.count;
        usersData.forEach((userData: any) => {
          const userInstance = new FactoryChild(userData);
          this.usersList.push(userInstance);
        });
        if (usersData.length === 0 && this.totalSize > 0) {
          this.pageNumber = 1;
          this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue};
          if (this.isDistributor) {
            this.router.navigate(['/distributor-users'], { queryParams: this.queryParams });
          } else {
            this.router.navigate(['/manufacturer-users'], { queryParams: this.queryParams });
          }
        }
      }
    });
  }

  handlePage(e: any) {
    this.pageNumber = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue};
    if (this.isDistributor) {
      this.router.navigate(['/distributor-users'], { queryParams: this.queryParams });
    } else {
      this.router.navigate(['/manufacturer-users'], { queryParams: this.queryParams });
    }
  }
  handleSearchClick() {
    if (this.showSearch && this.usersList.length > 0) {
      this.doSearch();
    } else {
      this.showSearch = true;
    }
  }
  doSearch() {
    let searchInput = this.searchForm.get('search')?.value || '';
    this.searchValue = searchInput.trim();
    this.searchForm.get('search')?.setValue(this.searchValue, { emitEvent: false });
    
    if (this.searchValue) {
      this.pageNumber = 1;
      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue};
      if (this.isDistributor) {
        this.router.navigate(['/distributor-users'], { queryParams: this.queryParams });
      } else {
        this.router.navigate(['/manufacturer-users'], { queryParams: this.queryParams });
      }
    }
  }

  clearSearch() {
    this.showSearch = !this.showSearch;
    this.searchForm.get('search')?.setValue('');
    this.searchValue = '';
    this.pageNumber = 1;
    this.queryParams = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchValue: this.searchValue
    };
    if (this.isDistributor) {
      this.router.navigate(['/distributor-users'], { queryParams: this.queryParams });
    } else {
      this.router.navigate(['/manufacturer-users'], { queryParams: this.queryParams });
    }
  }

  addUser() {
    const dialogRef = this.dialog.open(AddEditUserComponent, {
      disableClose: true,
      width: '400px',
      data: {
        type: 'Add',
      }
    });
    dialogRef.afterClosed().subscribe((newUser) => {
      if (newUser) {
        this.totalSize = this.totalSize + 1;
        this.usersList.unshift(newUser);
        if (this.usersList.length <= this.pageSize) {
        } else {
          this.usersList.splice(this.usersList.length - 1, 1);
        }
      }
    });
  }

  ngDoCheck() {
    if (this.utils.isDarkMode()) {
      this.imageSrc = '../../../assets/images/distributor-dark.png';
    } else {
      this.imageSrc = '../../../assets/images/distributor-light.png';
    }
  }

  editUser(id: any, user: FactoryChild) {
    const dialogRef = this.dialog.open(AddEditUserComponent, {
      disableClose: true,
      width: '400px',
      data: {
        type: 'Edit',
        id,
        user
      }
    });
    dialogRef.afterClosed().subscribe((editedUser) => {
      if (editedUser && editedUser.id) {
        this.usersList.forEach(User => {
          if (parseInt(User.id.toString(), 10) === editedUser.id) {
            User.firstName = editedUser.firstName;
            User.lastName = editedUser.lastName;
            User.email = editedUser.email;
            if (editedUser.picturePath !== undefined) {
              User.picturePath = editedUser.picturePath;
            }
          }
        });
      }
    });
  }

  deleteUser(id: any) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        width: '400px',
        data: {
          title: 'Delete',
          page: 'user',
        }
      });
      dialogRef.afterClosed().subscribe((status) => {
        if (status) {
          if (status === 'accept') {
            let accountType = 'manufacturer/DeleteManufacturerChild/';
            if (this.isDistributor) {
              accountType = 'distributor/DeleteDistributorChild/';
            }
            this.dataService.delete(accountType + id).then((data: any) => {
              if (data.statusCode === 200) {
                this.usersList.forEach( (users, index) => {
                  if (users.id === id) {
                    this.usersList.splice(index, 1);
                    if (this.usersList.length === 0 && this.pageNumber > 1) {
                      this.pageNumber = this.pageNumber - 1;
                      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue};
                      if (this.isDistributor) {
                        this.router.navigate(['/distributor-users'], { queryParams: this.queryParams });
                      } else {
                        this.router.navigate(['/manufacturer-users'], { queryParams: this.queryParams });
                      }
                    } else {
                      this.getUsers();
                    }
                  }
                });
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                  disableClose: true,
                  width: '400px',
                  data: {
                    title: 'Success',
                    type: 'Success',
                    message: 'User deleted successfully',
                    buttonText: 'Ok'
                  }
                });
              } else {
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                  disableClose: true,
                  width: '400px',
                  data: {
                    title: 'Error',
                    type: 'Error',
                    message: 'Something went wrong, Please try again!',
                    buttonText: 'Ok'
                  }
                });
              }
            });
          }
        }
      });
    }

    truncateName(firstName: string, lastName: string) {
      let fullName = firstName + ' ' + lastName;
      if (fullName.length  > 12) {
        fullName = fullName.slice(0, 12) + '...';
      }
      return fullName;
    }

  exportChildren(fileTypeId: number) {
    if(this.isDistributor) {
      this.exportService.exportFile(`distributor/ExportChildren/${this.userID}`, fileTypeId, this.searchValue, 'distributorUsers');
    }
    else{
    this.exportService.exportFile(`manufacturer/ExportChildren/${this.userID}`, fileTypeId, this.searchValue, 'manufacturerUsers');
    }
  }
}
